/**
 * @file DataElementPanels.tsx
 * @description Панели «Цифры» и «Таблица» (phase19.16 Р2; решение
 * пользователя 2026-10-10): цифры — до 4 пар «Число / Подпись», в ряд
 * [2][3][4], размер [Обычный][Крупный]; таблица — столбцы (до 6, заголовки;
 * новый столбец добавляет пустую ячейку в каждую строку), строки (до 30,
 * ячейки по столбцам, перетаскивание), «Первый столбец жирным», на телефоне
 * [Прокрутка][Карточками].
 * SB120 этап 3б: цифры — в ряд 1–6 числом (`GridCountField`, ↺ — число
 * показателей); у таблицы фон шапки, полосы и линии — в группе «Цвет и
 * шрифт» (`TableLookFields`).
 */

import { GRID_PER_ROW, SECTION_TEXT_LIMITS, SITE_LIMITS, type GridPerRow, type SectionElementOf } from "@chaiberi/shared";
import { Plus, X } from "lucide-react";

import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { CheckRow } from "../../../site-view/CheckRow";
import { Segmented } from "../../../site-view/Segmented";
import { SectionTextField } from "../fields/SectionTextField";
import { SortableList } from "../fields/SortableList";
import { useSectionForm } from "../section-form-context";
import { GridCountField } from "./GridCountField";
import type { ElementPanelProps } from "./types";
import { nextItemId } from "../../../../lib/site-builder/section-edit";

type Stat = SectionElementOf<"stats">["items"][number];
type Row = SectionElementOf<"table">["rows"][number];
const T = SECTION_TEXT_LIMITS;

/** «↺» у цифр — число показателей (как при добавлении), в пределах 1–6. */
const statsFallback = (count: number): GridPerRow => GRID_PER_ROW.find((n) => n === Math.min(Math.max(count, 1), 6)) ?? 3;

export function StatsPanel({ element, onChange, path }: ElementPanelProps<"stats">) {
  const { hasIssuesUnder } = useSectionForm();
  const set = (patch: Partial<typeof element>) => onChange({ ...element, ...patch });
  return (
    <>
      <SortableList<Stat>
        label="Цифры"
        items={element.items}
        max={SITE_LIMITS.statsItems}
        addLabel="Цифра"
        makeItem={() => ({ id: nextItemId(element.items, "s"), value: "100", label: "Подпись" })}
        onChange={(items) => set({ items })}
        invalidAt={(i) => hasIssuesUnder(`${path}.items.${i}`)}
        summary={(s) => `${s.value} — ${s.label}`}
        renderItem={(s, update, i) => (
          <>
            <SectionTextField label="Число" path={`${path}.items.${i}.value`} value={s.value} max={T.statValue} placeholder="15 лет, 2000+, 98 %" onChange={(value) => update({ ...s, value })} />
            <SectionTextField label="Подпись" path={`${path}.items.${i}.label`} value={s.label} max={T.statLabel} onChange={(label) => update({ ...s, label })} />
          </>
        )}
      />
      <GridCountField kind="stats" field="perRow" values={GRID_PER_ROW} value={element.perRow} fallback={statsFallback(element.items.length)} inheritLabel="по числу цифр" path={path} onChange={(perRow) => set({ perRow: perRow ?? statsFallback(element.items.length) })} />
      <Label>Размер</Label>
      <Segmented aria-label="Размер цифр" options={[{ value: "m" as const, label: "Обычный" }, { value: "l" as const, label: "Крупный" }]} value={element.size} onChange={(size) => set({ size })} />
    </>
  );
}

export function TablePanel({ element, onChange, path }: ElementPanelProps<"table">) {
  const { hasIssuesUnder } = useSectionForm();
  const set = (patch: Partial<typeof element>) => onChange({ ...element, ...patch });
  const cols = element.columns;
  const addColumn = () => set({ columns: [...cols, { id: nextItemId(cols, "c"), title: "" }], rows: element.rows.map((r) => ({ ...r, cells: [...r.cells, ""] })) });
  const removeColumn = (index: number) =>
    set({ columns: cols.filter((_, i) => i !== index), rows: element.rows.map((r) => ({ ...r, cells: r.cells.filter((_, i) => i !== index) })) });
  return (
    <>
      <Label>
        Столбцы <span className="type-caption text-on-surface-variant">{cols.length} из {SITE_LIMITS.tableColumns}</span>
      </Label>
      {cols.map((c, i) => (
        <div key={c.id} className="flex items-center gap-1">
          <Input aria-label={`Заголовок столбца ${i + 1}`} value={c.title} maxLength={T.tableTitle} placeholder={`Столбец ${i + 1}`} onChange={(e) => set({ columns: cols.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) })} />
          <Button variant="ghost" size="icon" className="size-12 shrink-0" aria-label={`Убрать столбец ${i + 1}`} disabled={cols.length <= 1} onClick={() => removeColumn(i)}>
            <X aria-hidden />
          </Button>
        </div>
      ))}
      {cols.length < SITE_LIMITS.tableColumns ? (
        <Button variant="outline" className="self-start" onClick={addColumn}>
          <Plus aria-hidden />
          Столбец
        </Button>
      ) : null}
      <SortableList<Row>
        label="Строки"
        items={element.rows}
        max={SITE_LIMITS.tableRows}
        addLabel="Строка"
        makeItem={() => ({ id: nextItemId(element.rows, "r"), cells: cols.map(() => "") })}
        onChange={(rows) => set({ rows })}
        invalidAt={(i) => hasIssuesUnder(`${path}.rows.${i}`)}
        summary={(r, i) => r.cells.find((c) => c.trim()) || `Строка ${i + 1}`}
        renderItem={(r, update) =>
          cols.map((c, ci) => (
            <div key={ci} className="flex flex-col gap-1">
              <Label>{c.title || `Столбец ${ci + 1}`}</Label>
              <Input value={r.cells[ci] ?? ""} maxLength={T.tableCell} onChange={(e) => update({ ...r, cells: cols.map((_, k) => (k === ci ? e.target.value : (r.cells[k] ?? ""))) })} />
            </div>
          ))
        }
      />
      <CheckRow checked={element.firstColumnBold} onChange={(firstColumnBold) => set({ firstColumnBold })}>
        Первый столбец жирным
      </CheckRow>
      <Label>На телефоне</Label>
      <Segmented aria-label="Таблица на телефоне" options={[{ value: "scroll" as const, label: "Прокрутка" }, { value: "stack" as const, label: "Карточками" }]} value={element.phoneLayout} onChange={(phoneLayout) => set({ phoneLayout })} />
    </>
  );
}
